import {Debounce} from '../../shared/tools/debounce';
import {gaLegacyCustomEvent} from '../ga/ga-legacy.functions';

const clearSpan = /(<span class="highlight">|<\/span>)/gim;
const highlightStartTag = `<span class='yellow-300-bg'>`;
const highlightEndTag = `</span>`;

export class FilterTable {
    private readonly tableElement: HTMLElement;
    private readonly tableElementID: string | HTMLElement;
    private readonly trArray: HTMLCollection | HTMLElement[];
    private readonly inputElement: HTMLInputElement;
    private readonly inputElementID: string;

    /**
     * Initialize a table filter
     * @param tableElementID
     * @param inputElementID
     */
    constructor(tableElementID: string | HTMLElement, inputElementID: string) {
        this.tableElementID = tableElementID;
        this.inputElementID = inputElementID;
        if (typeof tableElementID === 'string') {
            // this is a table
            this.tableElement = document.getElementById(tableElementID);
        } else {
            this.tableElement = tableElementID;
        }
        if (this.tableElement) {
            if (typeof this.tableElement.children[1] === 'undefined') {
                this.trArray = this.tableElement.children[0].children;
            } else {
                this.trArray = this.tableElement.children[1].children;
            }
        }

        this.inputElement = document.getElementById(inputElementID) as HTMLInputElement;
        this.initListeners();
    }

    /**
     * highlightInElements
     * @param {Array<HTMLElement>} elementArray array of TD elements
     * @param {string} searchTerm
     * @returns {boolean} true if any highlights were created/found
     */
    private static highlightInElements(elementArray: HTMLCollection, searchTerm: string): boolean {
        let hasSomeHighlights = false;

        for (const tdID in elementArray) {
            if (elementArray.hasOwnProperty(tdID)) {
                const tdElement = elementArray[tdID];
                const originalContent = tdElement.innerHTML;
                if (searchTerm === '') {
                    tdElement.innerHTML = originalContent.replace(clearSpan, '');
                    hasSomeHighlights = true;
                } else {
                    const {newText, foundText} = FilterTable.highlightInHTML(originalContent, searchTerm);
                    if (foundText) {
                        tdElement.innerHTML = newText;
                        hasSomeHighlights = true;
                    }
                }
            }
        }
        return hasSomeHighlights;
    }

    private static highlightInHTML(bodyText: string, searchTerm: string) {
        // find all occurrences of the search term in the given text,
        // and add some "highlight" tags to them (we're not using a
        // regular expression search, because we want to filter out
        // matches that occur within HTML tags and script blocks, so
        // we have to do a little extra validation)
        let newText = '';
        let foundText = false;
        let i = -1;

        const lcSearchTerm = searchTerm.toLowerCase();
        bodyText = bodyText.replace(clearSpan, '');
        let lcBodyText = bodyText.toLowerCase();
        while (bodyText.length > 0) {
            i = lcBodyText.indexOf(lcSearchTerm, i + 1);
            if (i < 0) {
                newText += bodyText;
                bodyText = '';
            } else {
                // skip anything inside an HTML tag
                if (bodyText.lastIndexOf('>', i) >= bodyText.lastIndexOf('<', i)) {
                    // skip anything inside a <script> block
                    if (lcBodyText.lastIndexOf('/script>', i) >= lcBodyText.lastIndexOf('<script', i)) {
                        newText +=
                            bodyText.substring(0, i) + highlightStartTag + bodyText.substring(i, i + searchTerm.length) + highlightEndTag;
                        bodyText = bodyText.substring(i + searchTerm.length);
                        lcBodyText = bodyText.toLowerCase();
                        i = -1;
                        foundText = true;
                    }
                }
            }
        }
        return {newText, foundText};
    }

    private doSearch = Debounce((text: string) => {
        const trackingArray = [];
        for (const tableRow in this.trArray) {
            if (this.trArray[tableRow].innerHTML) {
                const currentElement = this.trArray[tableRow] as HTMLElement;
                let currentChildren: any = currentElement.children;
                if (currentElement.tagName.toLowerCase() !== 'tr') {
                    currentChildren = [currentElement];
                }
                const foundSomeHighlights = FilterTable.highlightInElements(currentChildren, text);
                if (foundSomeHighlights) {
                    trackingArray.push(currentElement);
                    currentElement.style.removeProperty('display');
                } else {
                    currentElement.style.display = 'none';
                }
            }
        }
        gaLegacyCustomEvent({
            eventAction: `${this.inputElementID}` as any,
            eventCategory: `${this.tableElementID}` as any,
            eventLabel: `${text} | ${trackingArray && trackingArray.length ? trackingArray.length : 0}`,
        });
    }, 500);

    private initListeners() {
        $(this.inputElement).on('keyup', () => {
            const elementValue = this.inputElement.value;
            this.doSearch(elementValue);
        });
    }
}
